/**
 * Reflects `window.history.state` into the `modalNavigationRef` exported by
 * `useModalNavigation` so the layout's "render this post as a modal vs a
 * full page?" decision becomes a real reactive subscription instead of a
 * fake one.
 *
 * Why a plugin: `history.state` is not reactive. We listen on
 * `router.afterEach` (which fires *after* `route.path` and `route.params`
 * have committed) and copy the relevant slice into the ref. Consumers read
 * the ref via `useModalNavigation()` and Vue's reactivity does the rest.
 *
 * Why `.client.ts`: history is a browser concept. SSR has no history stack —
 * a fresh server response naturally has no "background page" — so the ref
 * stays empty on the server and the layout renders full-page, which is the
 * correct first-paint for an externally-pasted URL.
 *
 * Lifecycle:
 *   - Initial mount: forcibly clear any persisted `modalBackground` from
 *     `history.state`. Browsers preserve `history.state` across refresh,
 *     and SSR rendered full-page (because it has no history). Clearing
 *     keeps the post-hydration view aligned with SSR — refresh = full page,
 *     no flicker, no hydration mismatch.
 *   - Every subsequent navigation: `router.afterEach` re-reads state from
 *     the new history entry. Each entry owns its own state, so back/forward
 *     restores the right value automatically.
 */
import { defineNuxtPlugin, useRouter } from 'nuxt/app'

import { modalNavigationRef } from '../composables/useModalNavigation'
import type { NavigationState } from '../types/navigation'

const readModalBackgroundFromHistory = (): string | undefined => {
  if (typeof window === 'undefined') return undefined
  const state = window.history.state as NavigationState | null
  const background = state?.modalBackground
  return typeof background === 'string' && background.length > 0 ? background : undefined
}

const clearPersistedModalBackground = () => {
  if (typeof window === 'undefined') return
  const state = window.history.state as NavigationState | null
  if (!state || !state.modalBackground) return
  // Strip `modalBackground` while preserving Vue Router's own state keys
  // (back, current, forward, replaced, position, scroll). Replace, don't push,
  // so the URL and history stack are unchanged.
  const { modalBackground: _drop, ...rest } = state
  window.history.replaceState(rest, '')
}

const normalizeParam = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.length > 0) return value
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
  return undefined
}

export default defineNuxtPlugin(() => {
  if (!process.client) {
    return
  }

  const router = useRouter()

  // Treat the very first client tick as "fresh load": clear any persisted
  // modalBackground so refresh / direct-paste URLs render full-page (matches
  // SSR, no hydration flicker). Subsequent in-app pushes will set their own
  // state.modalBackground via CPNavigateTo.
  clearPersistedModalBackground()

  const initialRoute = router.currentRoute.value
  modalNavigationRef.value = {
    background: readModalBackgroundFromHistory(),
    currentFullPath: initialRoute.fullPath,
    currentPostId: normalizeParam(initialRoute.params.postId),
    currentGroupId: normalizeParam(initialRoute.query.groupId),
    currentLocationId: normalizeParam(initialRoute.query.locationId),
  }

  router.afterEach(to => {
    modalNavigationRef.value = {
      background: readModalBackgroundFromHistory(),
      currentFullPath: to.fullPath,
      currentPostId: normalizeParam(to.params.postId),
      currentGroupId: normalizeParam(to.query.groupId),
      currentLocationId: normalizeParam(to.query.locationId),
    }
  })
})
